import { useEffect } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { generateOrganizationSchema, generateProductSchema } from '@/lib/schema';
import { Navigation } from '@/components/Navigation';
import { Hero } from '@/components/Hero';
import { About } from '@/components/About';
import { Products } from '@/components/Products';
import { HowItWorks } from '@/components/HowItWorks';
import { Compliance } from '@/components/Compliance';
import { Resources } from '@/components/Resources';
import { FAQ } from '@/components/FAQ';
import { Contact } from '@/components/Contact';
import { Footer } from '@/components/Footer';

export default function Index() {
  const { language, t } = useLanguage();

  useEffect(() => {
    // Update document title and meta description based on language
    const title = language === 'en' 
      ? 'Chemical Indicators for Sterilization | Medical Sterilization Systems Co.,Ltd.'
      : 'ตัวบ่งชี้ทางเคมีสำหรับการทำให้ปราศจากเชื้อ | เมดิคอล สเตอริไลเซชั่น ซิสเต็มส์ จำกัด';
    
    const description = language === 'en'
      ? 'Manufacturer of chemical indicators for sterilization monitoring in hospitals, dental clinics, and healthcare facilities. Established May 2023 in Bangkok, Thailand.'
      : 'ผู้ผลิตตัวบ่งชี้ทางเคมีสำหรับเฝ้าระวังกระบวนการทำให้ปราศจากเชื้อในโรงพยาบาล คลินิกทันตกรรม และสถานพยาบาล ก่อตั้ง พ.ค. 2566 กรุงเทพฯ ประเทศไทย';

    document.title = title;
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    } else {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      metaDescription.setAttribute('content', description);
      document.head.appendChild(metaDescription);
    }

    // Update Open Graph meta tags
    const updateOrCreateMeta = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (meta) {
        meta.setAttribute('content', content);
      } else {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    };

    updateOrCreateMeta('og:title', title);
    updateOrCreateMeta('og:description', description);
    updateOrCreateMeta('og:type', 'website');
    updateOrCreateMeta('og:url', window.location.href);
    updateOrCreateMeta('og:image', 'https://mss.in.th/og-image.jpg');

    // Twitter Card meta tags
    updateOrCreateMeta('twitter:card', 'summary_large_image');
    updateOrCreateMeta('twitter:title', title);
    updateOrCreateMeta('twitter:description', description);
    updateOrCreateMeta('twitter:image', 'https://mss.in.th/og-image.jpg');

    // Add structured data (Schema.org)
    const addStructuredData = (schema: object, id: string) => {
      // Remove existing schema if present
      const existing = document.getElementById(id);
      if (existing) {
        existing.remove();
      }

      // Add new schema
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    };

    addStructuredData(generateOrganizationSchema(language), 'organization-schema');
    addStructuredData(generateProductSchema(language), 'product-schema');

    // Update lang attribute
    document.documentElement.lang = language;

  }, [language]);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Navigation />

      {/* Main Content */}
      <main>
        <Hero />
        <About />
        <Products />
        <HowItWorks />
        <Compliance />
        <Resources />
        <FAQ />
        <Contact />
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}